/**
 * ESCHER FALLS — 共有する一枚を組む。
 *
 * ## なぜ入れたか
 * 広告を買う金は無い。**絵そのものが宣伝になる**遊びなので、
 * 通した人が自分の一枚を出せるようにするのがいちばん安い流入口になる。
 *
 * ## 何を写すか
 * 盤面の実写（WebGL の画）をそのまま使う。縮図（`solutionPath.ts`）でも
 * 絵にはなるが、**あれは正解の線であって、通ったときの眺めではない。**
 * 人が見せたいのは「自分が見つけた角度」の方である。
 *
 * ## 出来た絵に何を書くか
 * 面の名前と時間だけ。順位も星も出さない——**比べるための遊びではない**ので、
 * 数字を大きく載せると別の遊びに見えてしまう。
 * 題名は下に小さく置く。誰の何かは分かればよい。
 */

import { LANG } from '../ui/i18n';

/** 版画の紙面に合わせた色。CSS 変数が読めない場所でも動くよう直に持つ。 */
const PAPER = '#f2efe6';
const INK = '#2b2823';
const INK_SOFT = '#6b665c';
const WATER = '#4a8c8a';

/** 4:5。縦長のほうが SNS の一覧で面積を取れる。 */
const W = 1080;
const H = 1350;

const SERIF =
  '"Hiragino Mincho ProN", "Yu Mincho", "Songti SC", Georgia, "Times New Roman", serif';

export interface ShareCardInput {
  /** 盤面の画（`canvas.toDataURL()`）。 */
  readonly boardPng: string;
  /** 「三 — 落ちる水」のような見出し。 */
  readonly title: string;
  /** 「01:24」のような時間。無ければ出さない。 */
  readonly time: string | null;
}

/** 画像を読む。読めなければ null（絵が無くても札は組める）。 */
function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/**
 * 盤面の写しから、実際に何か描かれている範囲だけを切り出す。
 *
 * **画面いっぱいを撮ると、城は真ん中の小さな塊になる。**
 * 端末の縦横比のまま貼ると余白ばかりの一枚になり、一覧では何も見えない。
 * 背景（隅の色）と違う画素の外接矩形を採って、そこだけ使う。
 *
 * 走査は 2 画素おき。1枚組むだけなので粗さは問題にならないし、
 * 端の 1 画素を取りこぼしても余白を足すので見た目に出ない。
 */
function cropToContent(img: HTMLImageElement): HTMLCanvasElement | null {
  const src = document.createElement('canvas');
  src.width = img.width;
  src.height = img.height;
  const sctx = src.getContext('2d', { willReadFrequently: true });
  if (sctx === null) return null;
  sctx.drawImage(img, 0, 0);

  let data: Uint8ClampedArray;
  try {
    data = sctx.getImageData(0, 0, src.width, src.height).data;
  } catch {
    return null; // 汚染された canvas。切らずに諦める。
  }

  const at = (x: number, y: number): number => (y * src.width + x) * 4;
  const bg = [data[0], data[1], data[2]];
  let minX = src.width;
  let minY = src.height;
  let maxX = -1;
  let maxY = -1;
  const STEP = 2;
  // 圧縮や反射で背景もわずかに揺れるので、少し鈍い閾値にする。
  const TOL = 10;
  for (let y = 0; y < src.height; y += STEP) {
    for (let x = 0; x < src.width; x += STEP) {
      const i = at(x, y);
      if (
        Math.abs(data[i] - bg[0]) <= TOL &&
        Math.abs(data[i + 1] - bg[1]) <= TOL &&
        Math.abs(data[i + 2] - bg[2]) <= TOL
      ) {
        continue;
      }
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null; // 何も写っていない。

  // 城の縁で断ち切らないよう、短辺の 4% ぶん余白を足す。
  const pad = Math.round(Math.min(src.width, src.height) * 0.04);
  const x0 = Math.max(0, minX - pad);
  const y0 = Math.max(0, minY - pad);
  const x1 = Math.min(src.width, maxX + pad);
  const y1 = Math.min(src.height, maxY + pad);

  const out = document.createElement('canvas');
  out.width = x1 - x0;
  out.height = y1 - y0;
  const octx = out.getContext('2d');
  if (octx === null) return null;
  octx.drawImage(src, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
  return out;
}

/**
 * 共有する一枚を組んで PNG の dataURL を返す。組めなければ null。
 *
 * **失敗しても遊びは何も変わらない。** 送れなかっただけである。
 */
export async function buildShareCard(input: ShareCardInput): Promise<string | null> {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return null;

  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, W, H);

  // 版画らしく、内側に細い枠を一本。断ち落としではなく余白を残す。
  const M = 56;
  ctx.strokeStyle = INK_SOFT;
  ctx.lineWidth = 2;
  ctx.strokeRect(M, M, W - M * 2, H - M * 2);

  // 盤面。枠の中に収まるよう縦横比を保って嵌める。
  const raw = await loadImage(input.boardPng);
  const board = raw === null ? null : (cropToContent(raw) ?? raw);
  const boxX = M + 40;
  const boxY = M + 40;
  const boxW = W - (M + 40) * 2;
  const boxH = 790;
  if (board !== null && board.width > 0 && board.height > 0) {
    const scale = Math.min(boxW / board.width, boxH / board.height);
    const dw = board.width * scale;
    const dh = board.height * scale;
    // **下端を揃える。** 城の高さは面ごとにまるで違うので、
    // 中央に置くと札ごとに見出しとの間隔が伸び縮みして落ち着かない。
    ctx.drawImage(board, boxX + (boxW - dw) / 2, boxY + (boxH - dh), dw, dh);
  }

  // 見出し。長い名前は詰めるのではなく**字を小さくする**（切ると意味が壊れる）。
  ctx.fillStyle = INK;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  let titleSize = 54;
  ctx.font = `${titleSize}px ${SERIF}`;
  while (ctx.measureText(input.title).width > boxW && titleSize > 28) {
    titleSize -= 2;
    ctx.font = `${titleSize}px ${SERIF}`;
  }
  ctx.fillText(input.title, W / 2, boxY + boxH + 118);

  // 時間。水の色で置く。**大きくしない**——競うための数字ではない。
  if (input.time !== null) {
    ctx.fillStyle = WATER;
    ctx.font = `40px ${SERIF}`;
    ctx.fillText(input.time, W / 2, boxY + boxH + 186);
  }

  // 誰の何か。下に小さく。
  ctx.fillStyle = INK_SOFT;
  ctx.font = `30px ${SERIF}`;
  ctx.fillText('ESCHER FALLS', W / 2, H - M - 76);
  ctx.font = `24px ${SERIF}`;
  ctx.fillText(
    LANG === 'ja' ? '視点を変えると、水路が繋がる' : 'Turn the view, and the water joins',
    W / 2,
    H - M - 34,
  );

  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

/** 共有に添える一言。 */
export function shareText(title: string, time: string | null): string {
  const t = time === null ? '' : ` — ${time}`;
  return LANG === 'ja'
    ? `ESCHER FALLS「${title}」${t} を通した`
    : `Cleared “${title}”${t} in ESCHER FALLS`;
}
